import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faArrowLeft,
  faTimes,
  faLightbulb,
  faQuestion
} from '@fortawesome/free-solid-svg-icons';

const GuidedTour = ({ isActive, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const overlayRef = useRef(null);
  const tooltipRef = useRef(null);

  const tourSteps = useMemo(() => [
    {
      target: '.compact-overview-container',
      title: 'Key Metrics Overview',
      description: 'These cards show the most important metrics at a glance: active pipelines, available EO tools, monitoring layers, and connected data sources.',
      position: 'bottom'
    },
    {
      target: '.ai-assistant-btn',
      title: '0. AI System Analysis',
      description: 'Click this AI Insights button to get real-time AI-powered analysis of your system metrics. The AI will analyze your current performance, identify potential issues, and provide actionable recommendations using Ollama running locally.',
      position: 'bottom'
    },
    {
      target: '#card-my-pipelines',
      title: '1. My Pipelines',
      description: 'This is your custom EO processing workflows dashboard. Here you can monitor which pipelines are currently running (like ECMWF→CDO or Sentinel-2→NDVI), which have completed successfully, and which have failed. Click on any pipeline to see detailed information including processing steps, data sources, and error details. This gives you immediate visibility into your most important processing workflows.',
      position: 'bottom'
    },
    {
      target: '#card-problems',
      title: '2. Data & Processing Errors',
      description: 'Here you see the errors happening right now in your stack. These include issues from your processing libraries (RasterIO, GDAL, HDF/NetCDF) as well as pipeline-specific failures like CRS mismatches or missing datasets. If something is red here, it means part of your processing is failing and needs attention.',
      position: 'bottom'
    },
    {
      target: '#card-stack-availability',
      title: '3. EO/GIS Service Health',
      description: 'This card shows the heartbeat of your services. GeoServer, PostGIS, STAC API, ingestion workers, pipelines, and even external providers — all checked here. Green means everything is working smoothly, yellow signals slowdowns, and red means downtime. This is where you quickly see if one of your building blocks is in trouble.',
      position: 'bottom'
    },
    {
      target: '#card-geospatial-files',
      title: '4. Data Storage & File Processing',
      description: 'Here\'s an overview of your geospatial files. You can see how much space your rasters and other formats are taking, which files are waiting to be processed, and whether any ingestions failed or produced corrupted files. If the pending queue grows, that\'s a sign your storage or ETL processes are lagging.',
      position: 'bottom'
    },
    {
      target: '#card-smartscape',
      title: '5. PostGIS Performance & Indexing',
      description: 'This is your database health check. You can see how many spatial indexes are active, how many queries are running slowly, and whether raster or vector queries are putting more load on PostGIS. If you notice spikes in slow queries, it may be time to optimize or add indexes.',
      position: 'bottom'
    },
    {
      target: '#card-external-sources',
      title: '6. External Data Sources Status',
      description: 'This card checks the upstream sources your pipelines depend on — like Copernicus SciHub, ESA Sentinel Hub, NASA EarthData, and USGS EarthExplorer. If they\'re down or under maintenance, you\'ll see it here. That way you know right away if ingestion failures are due to your system or an external provider.',
      position: 'bottom'
    },
    {
      target: '#card-real-user-monitoring',
      title: '7. Web Services Layers',
      description: 'Here you can see what your users are doing with your portal. It shows both your raw layers like Sentinel-2 or DEMs, and your derived products like NDVI, Biomass, or Yield Estimation. You also see how fast these layers respond and if users are hitting errors. This helps you understand which layers are most valuable to your users.',
      position: 'bottom'
    },
    {
      target: '#card-services',
      title: '8. Mission Data Ingestion',
      description: 'This card tracks the flow of data coming in from each mission. You can see how many Sentinel, Landsat, MODIS, and Copernicus products are being ingested, their success rates, and common issues like missing bands or corrupted metadata. If something slows down here, you\'ll know which mission\'s ingestion pipeline is struggling.',
      position: 'bottom'
    },
    {
      target: '#card-docker',
      title: '9. Processing Containers Health',
      description: 'These are the containers running your EO/GIS jobs. You can see how many are active, which ones are stuck in crash loops, and whether any images are outdated. If a pipeline is failing, this is where you\'ll quickly notice a worker crashing or being overloaded.',
      position: 'bottom'
    },
    {
      target: '#card-hosts',
      title: '10. Compute Resources',
      description: 'This card shows the raw power behind your stack. CPU, GPU, memory, and disk I/O across your processing nodes. If disk I/O is spiking, it usually means raster-heavy jobs are slowing the system. Watch this to know when your nodes are overloaded or when it\'s time to scale.',
      position: 'top'
    },
    {
      target: '#card-database',
      title: '11. Database Workload',
      description: 'Here\'s what your database is handling right now. Transactions per second, average response time, and the split between raster and vector queries. If raster-heavy queries start dominating, you might notice overall slowdowns here. This helps you stay ahead of DB bottlenecks.',
      position: 'bottom'
    },
    {
      target: '#card-network-status',
      title: '12. Data Transfer & Client Load',
      description: 'This is how your data is flowing to your users. You can see bandwidth consumption, how many clients are connected (via GeoServer, QGIS, or APIs), and whether any transfers failed or timed out. If users are reporting slow maps, this is the first place to check.',
      position: 'bottom'
    }
  ], []);

  useEffect(() => {
    if (isActive) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [isActive]);

  const updateTooltipPosition = useCallback(() => {
    const step = tourSteps[currentStep];
    const targetElement = document.querySelector(step.target);
    const tooltip = tooltipRef.current;

    if (!targetElement || !tooltip) {
      return;
    }

    // Force tooltip to be visible temporarily to get accurate measurements
    tooltip.style.visibility = 'visible';
    tooltip.style.opacity = '1';

    const targetRect = targetElement.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 30;

    let top, left, position = step.position;

    // Special case for first step - center the tooltip
    if (currentStep === 0) {
      top = (viewportHeight - tooltipRect.height) / 2;
      left = (viewportWidth - tooltipRect.width) / 2;

      tooltip.style.top = `${top}px`;
      tooltip.style.left = `${left}px`;

      // Add highlight to target element
      document.querySelectorAll('.tour-highlight').forEach(el => {
        el.classList.remove('tour-highlight');
      });

      if (targetElement) {
        targetElement.classList.add('tour-highlight');
        targetElement.style.position = 'relative';
        targetElement.style.zIndex = '9999';
      }

      return;
    }

    // Smart positioning - try the preferred position first, then fallback options
    const tryPosition = (pos) => {
      switch (pos) {
        case 'right':
          return {
            top: targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2),
            left: targetRect.right + margin,
            fits: targetRect.right + margin + tooltipRect.width <= viewportWidth - margin
          };
        case 'left':
          return {
            top: targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2),
            left: targetRect.left - tooltipRect.width - margin,
            fits: targetRect.left - tooltipRect.width - margin >= margin
          };
        case 'bottom':
          return {
            top: targetRect.bottom + margin,
            left: targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2),
            fits: targetRect.bottom + margin + tooltipRect.height <= viewportHeight - margin
          };
        case 'top':
          return {
            top: targetRect.top - tooltipRect.height - margin,
            left: targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2),
            fits: targetRect.top - tooltipRect.height - margin >= margin
          };
      }
    };

    // Try preferred position first
    let positioning = tryPosition(position);

    // If preferred position doesn't fit, try alternatives
    if (!positioning.fits) {
      const alternatives = ['bottom', 'top', 'right', 'left'].filter(p => p !== position);
      for (const altPos of alternatives) {
        const altPositioning = tryPosition(altPos);
        if (altPositioning.fits) {
          positioning = altPositioning;
          break;
        }
      }
    }

    top = positioning.top;
    left = positioning.left;

    // Final safety check - ensure tooltip is never positioned off-screen
    left = Math.max(margin, Math.min(left, viewportWidth - tooltipRect.width - margin));
    top = Math.max(margin, Math.min(top, viewportHeight - tooltipRect.height - margin));

    tooltip.style.top = `${top}px`;
    tooltip.style.left = `${left}px`;

    // Add highlight to target element
    document.querySelectorAll('.tour-highlight').forEach(el => {
      el.classList.remove('tour-highlight');
    });

    if (targetElement) {
      targetElement.classList.add('tour-highlight');
      // Force style to ensure visibility
      targetElement.style.position = 'relative';
      targetElement.style.zIndex = '9999';
    }

  }, [tourSteps, currentStep]);

  useEffect(() => {
    if (isVisible && currentStep < tourSteps.length) {
      // Give more time for tooltip content to render fully
      setTimeout(updateTooltipPosition, 200);
      // Additional check after a longer delay
      setTimeout(updateTooltipPosition, 500);
    }
  }, [currentStep, isVisible, tourSteps.length, updateTooltipPosition]);

  const nextStep = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTour();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const skipTour = () => {
    document.querySelectorAll('.tour-highlight').forEach(el => {
      el.classList.remove('tour-highlight');
      el.style.position = '';
      el.style.zIndex = '';
    });
    onClose();
  };

  const completeTour = () => {
    document.querySelectorAll('.tour-highlight').forEach(el => {
      el.classList.remove('tour-highlight');
      el.style.position = '';
      el.style.zIndex = '';
    });
    onComplete();
    onClose();
  };

  if (!isVisible) return null;

  const currentStepData = tourSteps[currentStep];

  return (
    <div className="guided-tour-overlay" ref={overlayRef}>
      <div className="tour-backdrop" onClick={skipTour} />

      <div className="tour-tooltip" ref={tooltipRef}>
        <div className="tour-header">
          <div className="tour-title">
            <FontAwesomeIcon icon={faLightbulb} />
            <span>{currentStepData.title}</span>
          </div>
          <button className="tour-close" onClick={skipTour}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="tour-content">
          <p>{currentStepData.description}</p>
        </div>

        <div className="tour-footer">
          <div className="tour-progress">
            <span>{currentStep + 1} of {tourSteps.length}</span>
            <div className="progress-dots">
              {tourSteps.map((_, index) => (
                <div
                  key={index}
                  className={`progress-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                />
              ))}
            </div>
          </div>

          <div className="tour-controls">
            {currentStep > 0 && (
              <button className="tour-btn secondary" onClick={prevStep}>
                <FontAwesomeIcon icon={faArrowLeft} />
                Previous
              </button>
            )}

            <button className="tour-btn skip" onClick={skipTour}>
              Skip Tour
            </button>

            <button className="tour-btn primary" onClick={nextStep}>
              {currentStep < tourSteps.length - 1 ? (
                <>
                  Next
                  <FontAwesomeIcon icon={faArrowRight} />
                </>
              ) : (
                'Complete Tour'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Tour trigger button component
export const TourTrigger = ({ onStartTour }) => {
  return (
    <button className="tour-trigger" onClick={onStartTour} title="Start Guided Tour">
      <FontAwesomeIcon icon={faQuestion} />
      <span>Take a Tour</span>
    </button>
  );
};

export default GuidedTour;